/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// INTERFACES
import { BankInterface, BankActionInterface } from "../../interfaces/Bank.interface";
// CONSTANTS
import { BLANK_BANK, BLANK_BANK_ACTION } from "../../constants/bank";
import { FormAction } from "../../types/enums";
import type { DeletedBankRow } from "../../utils/deletedBanks";

/**
 * State of the bank slice: the bank currently being viewed or edited,
 * the active selection, and the open/closed flags for this area's modals.
 */
export interface BankState {
  bank: BankInterface;
  idBankSelected: string;
  offsetsTable: Array<any>;
  generateOffsetsConfirm: boolean;
  openModalGenerateOffsets: boolean;
  nameBankSelected: string;
  openModalCreateEditBank: boolean;
  openModalDeleteBank: boolean;
  openModalDeleteBankAction: boolean;
  openModalRestoreBank: boolean;
  /** The deleted bank the restore modal is about; null while no restore is in progress. */
  bankToRestore: DeletedBankRow | null;
  openModalOffsetsTable: boolean;
  openModalActivateDeactivate: boolean;
  openDrawerBank: boolean;
  openDrawerBankFloorNames: boolean;
  bankRefreshTable: boolean;
  bankLiftsRefreshTable: boolean;
  bankBreadcrumb: Array<any>;
  bankUpdateBreadcrumb: any;
  clearFilters: boolean;
  openDrawerAssignActions: boolean;
  formAction: FormAction;
  bankAction: BankActionInterface;
  openForm: boolean;
  openModalBankActionsHelp: boolean;
}

const initialState: BankState = {
  bank: { ...BLANK_BANK },
  idBankSelected: "",
  offsetsTable: [],
  generateOffsetsConfirm: false,
  openModalGenerateOffsets: false,
  nameBankSelected: "",
  openModalCreateEditBank: false,
  openModalDeleteBank: false,
  openModalDeleteBankAction: false,
  openModalRestoreBank: false,
  bankToRestore: null,
  openModalOffsetsTable: false,
  openModalActivateDeactivate: false,
  openDrawerBank: false,
  openDrawerBankFloorNames: false,
  bankRefreshTable: false,
  bankLiftsRefreshTable: false,
  bankBreadcrumb: [],
  bankUpdateBreadcrumb: {
    update: false,
    id: "",
    name: "",
  },
  clearFilters: false,
  openDrawerAssignActions: false,
  formAction: FormAction.Create,
  bankAction: { ...BLANK_BANK_ACTION },
  openForm: false,
  openModalBankActionsHelp: false,
};

export const bankSlice = createSlice({
  name: "bank",
  initialState,
  reducers: {
    updateBank: (state, action: PayloadAction<BankInterface>) => {
      state.bank = action.payload;
    },
    updateIdBankSelected: (state, action: PayloadAction<string>) => {
      state.idBankSelected = action.payload;
    },
    updateOffsetsTable: (state, action: PayloadAction<any>) => {
      state.offsetsTable = action.payload;
    },
    updateGenerateOffsetsConfirm: (state, action: PayloadAction<boolean>) => {
      state.generateOffsetsConfirm = action.payload;
    },
    updateOpenModalGenerateOffsets: (state, action: PayloadAction<boolean>) => {
      state.openModalGenerateOffsets = action.payload;
    },
    updateNameBankSelected: (state, action: PayloadAction<string>) => {
      state.nameBankSelected = action.payload;
    },
    updateOpenModalCreateEditBank: (state, action: PayloadAction<boolean>) => {
      state.openModalCreateEditBank = action.payload;
    },
    updateOpenModalDeleteBank: (state, action: PayloadAction<boolean>) => {
      state.openModalDeleteBank = action.payload;
    },
    updateOpenModalDeleteBankAction: (state, action: PayloadAction<boolean>) => {
      state.openModalDeleteBankAction = action.payload;
    },
    updateOpenModalRestoreBank: (state, action: PayloadAction<boolean>) => {
      state.openModalRestoreBank = action.payload;
    },
    updateBankToRestore: (state, action: PayloadAction<DeletedBankRow | null>) => {
      state.bankToRestore = action.payload;
    },
    updateOpenModalOffsetsTable: (state, action: PayloadAction<boolean>) => {
      state.openModalOffsetsTable = action.payload;
    },
    updateOpenModalActivateDeactivate: (state, action: PayloadAction<boolean>) => {
      state.openModalActivateDeactivate = action.payload;
    },
    updateOpenDrawerBank: (state, action: PayloadAction<boolean>) => {
      state.openDrawerBank = action.payload;
    },
    updateOpenDrawerBankFloorNames: (state, action: PayloadAction<boolean>) => {
      state.openDrawerBankFloorNames = action.payload;
    },
    updateBankRefreshTable: (state, action: PayloadAction<boolean>) => {
      state.bankRefreshTable = action.payload;
    },
    updateBankLiftsRefreshTable: (state, action: PayloadAction<boolean>) => {
      state.bankLiftsRefreshTable = action.payload;
    },
    updateBankBreadcrumb: (state, action: PayloadAction<any>) => {
      state.bankBreadcrumb = action.payload;
    },
    updateBankUpdateBreadcrumb: (state, action: PayloadAction<any>) => {
      state.bankUpdateBreadcrumb = action.payload;
    },
    updateClearFilters: (state, action: PayloadAction<boolean>) => {
      state.clearFilters = action.payload;
    },
    updateFormAction: (state, action: PayloadAction<FormAction>) => {
      state.formAction = action.payload;
    },
    updateBankAction: (state, action: PayloadAction<BankActionInterface>) => {
      state.bankAction = action.payload;
    },
    updateOpenForm: (state, action: PayloadAction<boolean>) => {
      state.openForm = action.payload;
    },
    updateOpenModalBankActionsHelp: (state, action: PayloadAction<boolean>) => {
      state.openModalBankActionsHelp = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  updateBank,
  updateIdBankSelected,
  updateOffsetsTable,
  updateGenerateOffsetsConfirm,
  updateOpenModalGenerateOffsets,
  updateNameBankSelected,
  updateOpenModalCreateEditBank,
  updateOpenModalDeleteBank,
  updateOpenModalDeleteBankAction,
  updateOpenModalRestoreBank,
  updateBankToRestore,
  updateOpenModalOffsetsTable,
  updateOpenModalActivateDeactivate,
  updateOpenDrawerBank,
  updateOpenDrawerBankFloorNames,
  updateBankRefreshTable,
  updateBankLiftsRefreshTable,
  updateBankBreadcrumb,
  updateBankUpdateBreadcrumb,
  updateClearFilters,
  updateFormAction,
  updateBankAction,
  updateOpenForm,
  updateOpenModalBankActionsHelp,
} = bankSlice.actions;

export default bankSlice.reducer;
